import { t } from '@vk-hr-tek/core/translations';

import { Widget as WidgetGen, WidgetPartition } from '@app/gen/dashboard';
import { TimezoneEmployee } from '@app/gen/settings';

import {
  StatusConfigItem,
  TaskConfig,
  SectionKey,
  SectionConfig,
} from '../types';

export const CASE_DECLENSIONS = [
  { id: 'nominative', name: 'Именительный', question: 'Кто?' },
  { id: 'genitive', name: 'Родительный', question: 'Кого?' },
  { id: 'dative', name: 'Дательный', question: 'Кому?' },
  { id: 'accusative', name: 'Винительный', question: 'Кого?' },
  { id: 'creative', name: 'Творительный', question: 'Кем?' },
  { id: 'prepositional', name: 'Предложный', question: 'О ком?' },
];

export const CEO_FIELDS = [
  'first_name',
  'last_name',
  'middle_name',
  'position',
] as const;

export const WIDGET_TITLES: Record<WidgetGen['type'], string> = {
  quick_actions: 'Быстрые действия',
  tasks: 'Список задач',
  partitions_company: 'Сервисы руководителя',
  get partitions_employee() {
    return t('settings.constants.widgetTitles.partitionsEmployee');
  },
  calendar: 'Мой календарь',
  links: 'Электронные сервисы',
  personal_links: 'Персональные ссылки',
  external: 'Внешние виджеты',
  banner: 'Баннер',
  news: 'Новости',
  surveys: 'Опросы',
  absences: 'Ближайшие отпуска',
  top_news: 'Главные новости',
  posts_feed: 'Лента публикаций',
  birthdays: 'Дни рождения',
};

export const DEADLINE_OPTIONS = [
  { value: 'node_active', label: 'От момента активации этапа' },
  { value: 'attribute', label: 'От атрибута' },
  { value: 'created_date', label: 'От момента создания заявки' },
];

export const TIMEZONES_STATUS_CONFIG: Record<
  TimezoneEmployee['status'],
  StatusConfigItem
> = {
  success: {
    text: 'Импортирован из 1С',
    color: 'green',
    textColor: 'accent.text.success',
  },
  waiting: {
    text: 'Ожидание данных',
    color: 'orange',
    textColor: 'accent.text.warning',
  },
  error: {
    text: 'Не определено',
    color: 'orange',
    textColor: 'accent.text.warning',
  },
  unknown: {
    text: 'Не передано',
    color: 'gray',
    textColor: 'text.light.tertirary',
  },
};

export const ROLE_MODEL_FIRST_STICKY_HEADER_TOP = 64;

export const IMAGE_WIDTH_SMALL = 100;

export const IMAGE_HEIGHT_SMALL = 56;

export const IMAGE_WIDTH_MEDIUM = 376;

export const IMAGE_HEIGHT_MEDIUM = 84;

export const TASKS_PARTITIONS: Partial<
  Record<WidgetPartition['type'], TaskConfig>
> = {
  events_employee: { side: 'employee', label: 'Заявки' },
  events_company: { side: 'company', label: 'Заявки' },
  vacation_employee: { side: 'employee', label: 'Графики отпусков' },
  vacation_company: { side: 'company', label: 'Графики отпусков' },
  policies: { side: 'employee', label: 'Корпоративные документы' },
};

export const TASKS_WIDGET = {
  employee: {
    key: 'employee',
    get title() {
      return t('settings.company.widgets.tasks.partitionTitle');
    },
    get label() {
      return t('settings.company.widgets.tasks.partitionLabel');
    },
  },
  company: {
    key: 'company',
    title: 'Настройка виджета для компании',
    label: 'Выберите сервисы, которые будут отображаться в виджете компании',
  },
};

export enum RoleModelNameEnum {
  TitleRow = 'title-row',
  Default = 'default',
}

export const SUBMIT_ERROR_TEXT = 'Что-то пошло не так. Попробуйте еще раз';
export const MIN_SPECIFIC_WEEKEND_DAYS_DESCRIPTION = [
  {
    title: 'Дней всего',
    description: 'Минимальное количество выходных',
    color: 'text.light.tertirary' as const,
  },
  {
    title: '0-5',
    description: '0',
  },
  {
    title: '6',
    description: '1',
  },
  {
    title: '7-12',
    description: '2',
  },
  {
    title: '13',
    description: '3',
  },
  {
    title: '14-19',
    description: '4',
  },
  {
    title: '20',
    description: '5',
  },
  {
    title: '21-26',
    description: '6',
  },
  {
    title: '27',
    description: '7',
  },
  {
    title: '28-31',
    description: '8',
  },
];

export const SECTIONS_CONFIG: Record<SectionKey, SectionConfig> = {
  top: {
    cols: 3,
    styles: { height: 92 },
    emptyText: 'Сюда можно добавить только виджет «Быстрые действия»',
    allowedWidgetType: 'quick_actions',
  },
  main: {
    cols: 3,
    styles: { minHeight: 328 },
    emptyText: 'Сюда можно добавить виджеты',
  },
  posts: {
    cols: 2,
    styles: { height: 92, width: '67%' },
    emptyText: 'Сюда можно добавить только виджет «Лента публикаций»',
    allowedWidgetType: 'posts_feed',
  },
};

export const COMPANY_BANNER_SETTINGS_TOOLTIP_TEXT = {
  name: 'Это название используется только в настройках, чтобы различать баннеры в списке',
  delete:
    'В баннере обязательно должен быть хотя бы один слайд. При добавлении следующего слайда удаление станет доступным',
  additional_image:
    'Изображение располагается в правой части слайда, поверх фона. Доступно только для размера баннера M и L',
};

export const IMAGE_VALIDATOR_MESSAGE =
  'Загрузите изображение JPEG, PNG не более 10MB';

export const GRID_CARD_SIZE_M = 2;
export const GRID_CARD_SIZE_S = 1;
export const PARTITIONS_DEFAULT_GRID_ROWS = 5;
export const PARTITIONS_DEFAULT_GRID_COLS = 2;
export const PARTITIONS_DRAG_ACTIVATION_DISTANCE = 5;

export const DROPPABLE_GRID_ID = 'services-grid';
export const EMPTY_CELL_ID_PREFIX = 'empty-cell-';

export const MAX_LINK_NAME_LENGTH = 50;

export const SWITCH_OPTIONS = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];

export const RADIO_BUTTON_SCHEDULE_OPTIONS = [
  { value: 'work', label: 'Рабочие дни' },
  { value: 'calendar', label: 'Календарные дни' },
];

export const RADIO_BUTTON_STRUCTURE_OPTIONS = [
  { value: 'operational', label: 'Управленческий' },
  { value: 'legal', label: 'Юридический' },
  { value: 'functional', label: 'Функциональный' },
];

export const RADIO_BUTTON_APPROVAL_OPTIONS = [
  {
    value: 'manager',
    get label() {
      return t(
        'settings.vacations.create.coordinationGroupApproverManagerLabel',
      );
    },
  },
  {
    value: 'assistant',
    get label() {
      return t(
        'settings.vacations.create.coordinationGroupApproverAssistantLabel',
      );
    },
  },
  {
    value: 'groups',
    get label() {
      return t(
        'settings.vacations.create.coordinationGroupApproverSpecialistLabel',
      );
    },
  },
];

export const CURRENT_YEAR = (new Date().getFullYear() + 1).toString();

export const COORDINATION_BLOCK_WIDTH = 572;

export const HANDLE_CHANGE_PLUG = () => {};

export const FORM_FIELD_GRID_ITEMS = 'items';
export const FORM_FIELD_AVAILABLE_ITEMS = 'availableItems';
export const FORM_FIELD_GRID_ROWS = 'gridRows';
export const FORM_FIELD_GRID_COLS = 'gridCols';

export const QUANTITY_TO_HIDE_UNAVAILABLE_TYPES = 2;
